import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { createTranslator, useLanguage } from '../i18n/LanguageContext';
import TeamBuildingIndividualRegistration from './TeamBuildingIndividualRegistration';
import TournamentRegistration from './TournamentRegistration';

const copy = {
  fr: { choice: 'Choisissez votre activité', hint: 'Cochez une option pour afficher le formulaire d’inscription.', initiation: 'Initiation (cours) — 1 personne', tournament: 'Participer au tournoi — 2 personnes', loading: 'Chargement...', unavailable: 'Cet événement est indisponible.' },
  en: { choice: 'Choose your activity', hint: 'Select an option to display the registration form.', initiation: 'Introductory lesson — 1 person', tournament: 'Join the tournament — 2 people', loading: 'Loading...', unavailable: 'This event is unavailable.' },
  nl: { choice: 'Kies je activiteit', hint: 'Selecteer een optie om het inschrijfformulier te tonen.', initiation: 'Initiatieles — 1 persoon', tournament: 'Deelnemen aan het toernooi — 2 personen', loading: 'Laden...', unavailable: 'Dit evenement is niet beschikbaar.' },
  de: { choice: 'Aktivität auswählen', hint: 'Wähle eine Option, um das Anmeldeformular anzuzeigen.', initiation: 'Einführungskurs — 1 Person', tournament: 'Am Turnier teilnehmen — 2 Personen', loading: 'Laden...', unavailable: 'Diese Veranstaltung ist nicht verfügbar.' },
};

export default function TeamBuildingRegistration({ legacyRoute = false }) {
  const params = useParams();
  const eventId = params.eventId || params.tournamentId;
  const { language } = useLanguage();
  const t = createTranslator(language, copy);
  const [state, setState] = useState({ loading: true, event: null });
  const [activity, setActivity] = useState('');

  useEffect(() => {
    let active = true;
    setState({ loading: true, event: null });
    setActivity('');
    getDoc(doc(db, 'team_building_catalog', eventId))
      .then((snapshot) => {
        if (active) setState({ loading: false, event: snapshot.exists() ? snapshot.data() : null, notFound: !snapshot.exists() });
      })
      .catch(() => { if (active) setState({ loading: false, event: null }); });
    return () => { active = false; };
  }, [eventId]);

  if (state.loading) return <main className="container mx-auto my-28 text-center">{t('loading')}</main>;
  // Standard tournaments share the historical registration URL, but have no team-building entry.
  if (legacyRoute && state.notFound) return <TournamentRegistration registrationTournamentId={eventId} />;
  const event = state.event;
  if (!event || event.visible === false || event.archived === true || event.registrationDisabled) {
    return <main className="container mx-auto my-28 text-center">{t('unavailable')}</main>;
  }
  if (event.registrationMode === 'individual') return <TeamBuildingIndividualRegistration registrationEventId={eventId} />;
  if (!event.initiationEnabled) return <TournamentRegistration registrationTournamentId={eventId} teamBuilding />;

  return <>
    <section className="mx-auto mt-28 max-w-3xl px-4">
      <h1 className="mb-4 text-3xl font-black text-[#4c2f8e]">{event.name}</h1>
      <fieldset className="space-y-4 rounded-2xl border border-purple-200 bg-purple-50 p-6">
        <legend className="text-xl font-bold text-[#4c2f8e]">{t('choice')}</legend>
        <p className="text-sm text-gray-600">{t('hint')}</p>
        {['initiation', 'tournament'].map((value) => <label key={value} className="flex cursor-pointer items-center gap-3 rounded-xl border bg-white p-4 font-semibold">
          <input type="radio" name="team-building-activity" value={value} checked={activity === value}
            onChange={() => setActivity(value)} className="h-5 w-5 accent-[#4c2f8e]" />
          {t(value)}
        </label>)}
      </fieldset>
    </section>
    {activity === 'initiation' && <TeamBuildingIndividualRegistration registrationEventId={eventId} />}
    {activity === 'tournament' && <TournamentRegistration registrationTournamentId={eventId} teamBuilding />}
  </>;
}
